import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Area, AreaChart, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Student, UsersThree, CalendarCheck, CurrencyInr, Warning, Megaphone, Notebook, Chalkboard, ArrowRight, Trophy, Cake, DownloadSimple, ShareNetwork } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { useApi, inr, fmtDate, api, errMsg } from "../lib/api";
import { PageHeader, StatCard, GlassCard, Loader, Badge, Empty, fadeUp } from "../components/kit";
import { PersonPhoto } from "../components/Photo";

const tip = { contentStyle: { background: "#0c0c16", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 14, color: "#fff" }, cursor: { fill: "rgba(255,255,255,0.03)" } };

function greeting(t) {
  const h = new Date().getHours();
  return t(h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");
}

const Panel = ({ title, eyebrow, children, className = "", action }) => (
  <GlassCard className={`p-6 sm:p-7 ${className}`}>
    <div className="mb-5 flex items-center justify-between">
      <div>
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h3 className="font-display text-lg md:text-xl font-semibold text-white">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </GlassCard>
);

const NoticeList = ({ notices }) => (notices?.length ? (
  <div className="space-y-3">
    {notices.map((n) => (
      <div key={n.id} className="rounded-2xl bg-white/[0.03] border border-white/5 p-4">
        <div className="flex items-center gap-2">{n.priority === "high" && <Badge status="high">Important</Badge>}<span className="text-xs text-zinc-500">{fmtDate(n.created_at)}</span></div>
        <div className="mt-1.5 text-sm font-semibold text-white">{n.title}</div>
        <div className="text-xs text-zinc-400 line-clamp-2">{n.body}</div>
      </div>
    ))}
  </div>
) : <Empty />);

async function cardFile(p) {
  const r = await api.get(`/birthdays/card/${p.kind}/${p.id}.png`, { responseType: "blob" });
  return new File([r.data], `Happy_Birthday_${p.name.replace(/\s+/g, "_")}.png`, { type: "image/png" });
}

function saveFile(file) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

function CardActions({ p, i, t }) {
  const [busy, setBusy] = useState(false);
  const run = async (share) => {
    setBusy(true);
    try {
      const file = await cardFile(p);
      if (share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Happy Birthday", text: `Happy Birthday, ${p.name}!` }).catch(() => {});
      } else {
        saveFile(file);
        toast.success(share ? t("Sharing isn't available here, so the card was downloaded") : t("Card downloaded"));
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const cls = "grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/5 text-zinc-300 transition-colors duration-200 hover:bg-white/15 hover:text-white disabled:opacity-40";
  return (
    <div className="ml-auto flex shrink-0 gap-1.5">
      <button className={cls} disabled={busy} onClick={() => run(false)} title={t("Download card")} aria-label={t("Download card")} data-testid={`birthday-download-${i}`}><DownloadSimple size={15} /></button>
      <button className={cls} disabled={busy} onClick={() => run(true)} title={t("Share card")} aria-label={t("Share card")} data-testid={`birthday-share-${i}`}><ShareNetwork size={15} /></button>
    </div>
  );
}

function Birthdays({ t, className = "" }) {
  const { data: d } = useApi("/birthdays/today");
  const people = d ? [...d.students.map((s) => ({ ...s, kind: "student", sub: `${s.class_name}${s.age ? ` · ${s.age} ${t("yrs")}` : ""}`, tone: "text-blue-300" })),
    ...d.staff.map((s) => ({ ...s, kind: "staff", sub: s.designation || t("Staff"), tone: "text-orange-300" }))] : [];
  return (
    <Panel title={t("Today's birthdays")} eyebrow={t("Celebrate")} className={className}>
      <div data-testid="birthdays-card">
        {!d ? <Loader /> : !people.length ? (
          <div className="flex flex-col items-center py-4 text-center text-sm text-zinc-500" data-testid="birthdays-empty"><Cake size={28} weight="duotone" className="mb-2 text-zinc-600" />{t("No birthdays today")}</div>
        ) : (
          <div className="max-h-[260px] space-y-2 overflow-y-auto">
            {people.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 rounded-2xl border border-white/5 bg-white/[0.03] px-3 py-2.5" data-testid={`birthday-item-${i}`}>
                <PersonPhoto kind={p.kind} person={p} className="h-10 w-10 text-xs" testid={`birthday-photo-${i}`} />
                <div className="min-w-0"><div className="truncate text-sm font-semibold text-white">{p.name}</div><div className={`text-xs ${p.tone}`}>{p.sub}</div></div>
                <CardActions p={p} i={i} t={t} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

function AdminDash({ t }) {
  const { data: d, loading } = useApi("/dashboard/admin");
  if (loading || !d) return <Loader />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard icon={Student} label={t("Total students")} value={d.students} hint={`${d.classes} ${t("Classes").toLowerCase()}`} testid="stat-students" />
      <StatCard icon={UsersThree} label={t("Teaching staff")} value={d.staff} tone="rose" testid="stat-staff" delay={0.05} />
      <StatCard icon={CalendarCheck} label={t("Today's attendance")} value={d.today_attendance != null ? `${d.today_attendance}%` : "—"} tone="green" hint={d.today_attendance == null ? "Not marked yet today" : "Across all classes"} testid="stat-attendance" delay={0.1} />
      <StatCard icon={CurrencyInr} label={t("Fee collected")} value={inr(d.fee_collected)} tone="orange" hint={`${inr(d.fee_pending)} ${t("Pending").toLowerCase()}`} testid="stat-fees" delay={0.15} />

      <Panel title={t("Attendance trend")} eyebrow="Last 10 school days" className="md:col-span-2 lg:col-span-3">
        <div className="h-[260px]" data-testid="attendance-trend-chart">
          <ResponsiveContainer>
            <AreaChart data={d.attendance_trend}>
              <defs><linearGradient id="ga" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis domain={[50, 100]} stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} width={34} />
              <Tooltip {...tip} />
              <Area type="monotone" dataKey="percent" name="Attendance %" stroke="#60A5FA" strokeWidth={2.5} fill="url(#ga)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title={t("Pending fees")} eyebrow="Outstanding" className="lg:col-span-1">
        <div className="font-display text-4xl font-semibold text-orange-300" data-testid="pending-fee-total">{inr(d.fee_pending)}</div>
        <p className="mt-2 text-sm text-zinc-400">{`${d.pending_invoices} invoices awaiting payment`}</p>
        <div className="mt-6 h-2 rounded-full bg-white/5 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${d.fee_billed ? (d.fee_collected * 100) / d.fee_billed : 0}%` }} transition={{ duration: 1.2, ease: "easeOut" }} className="h-full bg-gradient-to-r from-blue-500 to-orange-400" />
        </div>
        <p className="mt-2 text-xs text-zinc-500">{`${d.fee_billed ? Math.round((d.fee_collected * 100) / d.fee_billed) : 0}% of ${inr(d.fee_billed)} collected`}</p>
        <Link to="/reports" className="btn-ghost mt-6 w-full" data-testid="view-pending-fees-link">{t("Reports")} <ArrowRight size={14} /></Link>
      </Panel>

      <Panel title={t("Fee collection")} eyebrow="Monthly" className="md:col-span-2">
        <div className="h-[220px]">
          <ResponsiveContainer>
            <BarChart data={d.collection_trend}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="month" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} width={50} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip {...tip} formatter={(v) => inr(v)} />
              <Bar dataKey="amount" fill="#F97316" radius={[10, 10, 4, 4]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
      <Panel title={t("Recent payments")} eyebrow="Live" className="md:col-span-2">
        {d.recent_payments.length ? (
          <div className="space-y-2" data-testid="recent-payments-list">
            {d.recent_payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-2xl px-3 py-2.5 hover:bg-white/[0.03] transition-colors duration-200">
                <div className="min-w-0"><div className="text-sm font-semibold text-white truncate">{p.student_name}</div><div className="text-xs text-zinc-500">{p.title} · {p.method}</div></div>
                <div className="text-right"><div className="text-sm font-semibold text-emerald-300">{inr(p.amount)}</div><div className="text-[11px] text-zinc-500">{fmtDate(p.paid_at)}</div></div>
              </div>
            ))}
          </div>
        ) : <Empty />}
      </Panel>
      <Panel title={t("Class strength")} className="md:col-span-1 lg:col-span-1">
        <div className="space-y-4">
          {d.class_strength.map((c) => (
            <div key={c.class}>
              <div className="flex justify-between text-sm"><span className="text-zinc-300">{c.class}</span><span className="text-white font-semibold">{c.students}</span></div>
              <div className="mt-2 h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-blue-400/80" style={{ width: `${Math.min(100, c.students * 2.5)}%` }} /></div>
            </div>
          ))}
        </div>
      </Panel>
      <Birthdays t={t} className="md:col-span-1 lg:col-span-1 self-start" />
      <Panel title={t("Latest notices")} className="md:col-span-2 lg:col-span-2"><NoticeList notices={d.recent_notices} /></Panel>
    </div>
  );
}

function TeacherDash({ t }) {
  const { data: d, loading } = useApi("/dashboard/teacher");
  if (loading || !d) return <Loader />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard icon={Chalkboard} label={t("Classes")} value={d.classes} testid="stat-classes" />
      <StatCard icon={Student} label={t("Students")} value={d.students} tone="orange" testid="stat-students" delay={0.05} />
      <StatCard icon={Warning} label={t("Attendance pending")} value={d.attendance_pending_today} tone="rose" hint="Classes not marked today" testid="stat-att-pending" delay={0.1} />
      <StatCard icon={Notebook} label={t("Homework posted")} value={d.my_homework} tone="green" testid="stat-homework" delay={0.15} />
      <Panel title="Quick actions" className="md:col-span-2">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[["/attendance", CalendarCheck, "Mark attendance"], ["/homework", Notebook, "Assign homework"], ["/exams", Trophy, "Enter marks"]].map(([to, I, l]) => (
            <Link key={to} to={to} className="glass glass-hover rounded-2xl p-5 block" data-testid={`quick-${to.slice(1)}`}>
              <I size={24} weight="duotone" className="text-blue-300" />
              <div className="mt-4 text-sm font-semibold text-white">{t(l)}</div>
            </Link>
          ))}
        </div>
      </Panel>
      <Panel title={t("Latest notices")} className="md:col-span-1"><NoticeList notices={d.notices} /></Panel>
      <Birthdays t={t} className="md:col-span-1" />
      <Panel title="My recent homework" className="md:col-span-2 lg:col-span-4">
        {d.recent_homework.length ? d.recent_homework.map((h) => (
          <div key={h.id} className="flex items-center justify-between border-t border-white/5 py-3 first:border-0">
            <div><div className="text-sm font-semibold text-white">{h.title}</div><div className="text-xs text-zinc-500">{h.subject}</div></div>
            <span className="text-xs text-zinc-400">{t("Due")} {fmtDate(h.due_date)}</span>
          </div>
        )) : <Empty />}
      </Panel>
    </div>
  );
}

function ChildCard({ k, t, isParent, i }) {
  return (
    <motion.div {...fadeUp(i)} className="md:col-span-2 lg:col-span-4">
      <GlassCard className="p-6 sm:p-8 relative overflow-hidden" data-testid={`child-card-${k.admission_no}`}>
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-gradient-to-br from-blue-500/20 to-orange-500/10 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
          <div className="lg:w-1/4">
            <div className="eyebrow">{k.class_name} · {t("Roll no")} {k.roll_no}</div>
            <div className="mt-2 font-display text-3xl font-semibold text-white">{k.name}</div>
            <div className="text-xs text-zinc-500 mt-1">{k.admission_no}</div>
          </div>
          <div className="grid flex-1 grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-white/5 border border-white/5 p-5">
              <div className="eyebrow !text-[10px]">{t("Attendance this month")}</div>
              <div className="mt-2 font-display text-3xl text-emerald-300" data-testid="child-attendance">{k.attendance_percent != null ? `${k.attendance_percent}%` : "—"}</div>
              <div className="text-xs text-zinc-500">{k.days_present}/{k.days_total} days</div>
            </div>
            {isParent && (
              <div className="rounded-2xl bg-white/5 border border-white/5 p-5">
                <div className="eyebrow !text-[10px]">{t("Fee due")}</div>
                <div className={`mt-2 font-display text-3xl ${k.fee_due > 0 ? "text-orange-300" : "text-emerald-300"}`} data-testid="child-fee-due">{inr(k.fee_due)}</div>
                {k.fee_due > 0 && <Link to="/fees" className="text-xs text-blue-300 hover:text-blue-200" data-testid="child-pay-link">{t("Pay now")} →</Link>}
              </div>
            )}
            <div className="rounded-2xl bg-white/5 border border-white/5 p-5">
              <div className="eyebrow !text-[10px]">{t("Latest result")}</div>
              <div className="mt-2 font-display text-3xl text-blue-300" data-testid="child-latest-result">{k.latest_result?.percent != null ? `${k.latest_result.percent}%` : "—"}</div>
              <div className="text-xs text-zinc-500">{k.latest_result ? `${k.latest_result.exam_name} · ${t("Grade")} ${k.latest_result.grade}` : "No results yet"}</div>
            </div>
          </div>
        </div>
      </GlassCard>
    </motion.div>
  );
}

function FamilyDash({ t, isParent }) {
  const { data, loading } = useApi("/my/overview");
  const { data: hw } = useApi("/homework");
  const { data: notices } = useApi("/notices");
  if (loading || !data) return <Loader />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {data.children.map((k, i) => <ChildCard key={k.id} k={k} t={t} isParent={isParent} i={i} />)}
      <Panel title={t("Homework")} eyebrow="Upcoming" className="md:col-span-1 lg:col-span-2" action={<Link to="/homework" className="text-xs text-blue-300">{t("View")} →</Link>}>
        {hw?.length ? hw.slice(0, 4).map((h) => (
          <div key={h.id} className="flex items-center justify-between border-t border-white/5 py-3 first:border-0">
            <div className="min-w-0"><div className="text-sm font-semibold text-white truncate">{h.title}</div><div className="text-xs text-zinc-500">{h.subject}</div></div>
            <span className="text-xs text-orange-300 whitespace-nowrap">{t("Due")} {fmtDate(h.due_date)}</span>
          </div>
        )) : <Empty />}
      </Panel>
      <Panel title={t("Notices")} className="md:col-span-1 lg:col-span-2" action={<Link to="/notices" className="text-xs text-blue-300"><Megaphone size={14} className="inline" /> {t("View")}</Link>}>
        <NoticeList notices={notices?.slice(0, 3)} />
      </Panel>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useT();
  const role = user.role;
  return (
    <div>
      <PageHeader eyebrow={`${user.school?.name || ""} · ${role}`} title={<>{greeting(t)}, <span className="text-gradient">{user.name.split(" ")[role === "admin" ? 1 : 0] || user.name}</span></>}
        subtitle={role === "admin" ? "Here's the pulse of your school today." : role === "teacher" ? "Your classes at a glance." : "Everything about your child's school life, in one place."} />
      {role === "admin" && <AdminDash t={t} />}
      {role === "teacher" && <TeacherDash t={t} />}
      {(role === "parent" || role === "student") && <FamilyDash t={t} isParent={role === "parent"} />}
    </div>
  );
}
